import React, { PropsWithChildren, createContext, useContext } from 'react';

import { PermissionStatus, createPermissionHook } from 'expo-modules-core';
import {
  IosAuthorizationStatus,
  NotificationPermissionsRequest,
  NotificationPermissionsStatus,
  getPermissionsAsync,
  requestPermissionsAsync,
} from 'expo-notifications';

import { useMemoAll } from 'utils/use-memo-all';

export interface IPushPermissionContext {
  isPushPermissionKnown: boolean;
  requestPermission: () => Promise<NotificationPermissionsStatus>;
  settings: NotificationPermissionsStatus | null;
  isLoadingPushPermission: boolean;
}

export const PushPermissionContext = createContext<IPushPermissionContext>(
  {} as IPushPermissionContext
);

export const usePushPermission = () => useContext<IPushPermissionContext>(PushPermissionContext);

// WARNING: This was removed from expo-notifications (Not exactly sure why)
// https://github.com/expo/expo/pull/31905/files
const usePermissions = createPermissionHook<
  NotificationPermissionsStatus,
  NotificationPermissionsRequest
>({
  requestMethod: requestPermissionsAsync,
  getMethod: getPermissionsAsync,
});

export const PushPermissionProvider = ({ children }: PropsWithChildren<{}>) => {
  const [settings, requestPermission] = usePermissions();

  const isPushPermissionKnown =
    !!settings &&
    settings?.status !== PermissionStatus.UNDETERMINED &&
    settings?.ios?.status !== IosAuthorizationStatus.NOT_DETERMINED;

  const isLoadingPushPermission = !settings;

  const value = useMemoAll({
    isPushPermissionKnown,
    requestPermission,
    settings,
    isLoadingPushPermission,
  });

  return <PushPermissionContext.Provider value={value}>{children}</PushPermissionContext.Provider>;
};
